import { i18n } from '@international/mastodon-i18n';
import {
  CandidateStories,
  PromptGroup,
  PromptGroupKind,
  PromptItem,
} from '../../../../../types/candidateStories';
import { stripEmojis } from '../../../../../lib/helpers';

export const getPromptGroupByKind = (
  promptGroups: PromptGroup[] | null,
  kind: PromptGroupKind,
): PromptGroup | undefined => {
  return promptGroups?.find(group => group.kind === kind);
};

export const getGeneralPromptGroup = (
  promptGroups: PromptGroup[] | null,
): PromptGroup | undefined => {
  return getPromptGroupByKind(promptGroups, PromptGroupKind.GENERAL);
};

export const getCriminalPromptGroup = (
  promptGroups: PromptGroup[] | null,
): PromptGroup | undefined => {
  return getPromptGroupByKind(promptGroups, PromptGroupKind.CRIMINAL);
};

export const findItemTranslation = (
  promptItem: PromptItem | null,
  lang: string,
) => {
  if (!promptItem) {
    return null;
  }

  return (
    promptItem.translations.find(t => t.language === lang) ||
    promptItem.translations.find(t => lang.startsWith(t.language)) ||
    promptItem.translations.find(t => t.language === 'en')
  );
};

export const getPromptItemById = (
  promptGroups: PromptGroup[],
  promptId: string,
) => {
  const allItems = promptGroups.flatMap(group => group.items);
  return allItems.find(item => item.id === promptId) || null;
};

export const getPromptDetails = (
  promptId: string,
  promptGroups: PromptGroup[] | null,
) => {
  if (!promptGroups || !Array.isArray(promptGroups)) return null;

  const currentLanguage = i18n.getLocale();
  for (const group of promptGroups) {
    const promptItem = group.items?.find(item => item.id === promptId);
    if (promptItem) {
      const translation = findItemTranslation(promptItem, currentLanguage);
      return {
        id: promptId,
        title: translation?.title || '',
      };
    }
  }
  return null;
};

export const formatCustomPromptContent = (
  promptResponses: Record<string, string>,
  promptGroups: PromptGroup[] | null,
) => {
  if (!promptResponses || typeof promptResponses !== 'object') return '';

  const promptArray: Array<{
    prompt: { id: string; title: string };
    response: string;
  }> = [];

  Object.entries(promptResponses).forEach(([promptId, response]) => {
    if (response && typeof response === 'string') {
      const promptDetails = getPromptDetails(promptId, promptGroups);
      if (promptDetails) {
        promptArray.push({
          prompt: promptDetails,
          response: stripEmojis(response),
        });
      }
    }
  });

  return promptArray.length > 0 ? JSON.stringify(promptArray) : '';
};

export const collectPromptDocuments = (
  recordId: string,
  candidateStories: any,
) => {
  if (
    !candidateStories.promptGroup ||
    !candidateStories.promptGroup[recordId]
  ) {
    return [];
  }

  const allDocuments: any[] = [];
  const recordPromptDocuments = candidateStories.promptGroup[recordId];

  Object.values(recordPromptDocuments).forEach((promptData: any) => {
    if (Array.isArray(promptData?.documents)) {
      allDocuments.push(...promptData.documents);
    }
  });

  return allDocuments;
};

// True when the item has neither prompt text nor prompt files, i.e. what
// submitCandidateStories would serialize for it carries no content or documents.
export const isPromptItemEmpty = (
  key: string,
  candidateStories: CandidateStories,
  promptGroups: PromptGroup[] | null,
) =>
  !formatCustomPromptContent(
    candidateStories.promptGroup?.content?.[key],
    promptGroups,
  ) && collectPromptDocuments(key, candidateStories).length === 0;
